<template>
  <label
    :class="classList"
    tabindex="0"
    @focus="focusIn"
    @focusout="focusOut"
    v-if="!native">
    <div class="m-dropdown__selected" v-if="map">{{ getTitle }}</div>
    <div class="m-dropdown__selected" v-else>
      {{ selectedItem.title || defaultTitle }}
    </div>
    <icon name="chevron-down" class="m-dropdown__icon"></icon>
    <div
      :class="active
        ? 'm-dropdown__list m-dropdown__list--active'
        : 'm-dropdown__list'"
      ref="list">
      <template v-for="(item, index) in list">
        <v-list-item
          ref="listItem"
          :id="item.id"
          :data-index="index"
          :title="map ? item[map.title] : item.title"
          :action="selectItem"
          :basic="true"
          :key="item.id"
          :selectFirst="true"></v-list-item>
      </template>
    </div>
  </label>
  <label :class="classList" v-else>
    <select tabindex="0">
      <option v-for="(item, index) in list" :value="item.title" :key="index">
        {{ item.title }}
      </option>
    </select>
    <icon name="chevron-down" class="m-dropdown__icon"></icon>
  </label>
</template>

<script>
import { VListItem } from '../atoms';

export default {
  name: 'v-dropdown',
  props: {
    list: {
      type: [Array],
      required: true,
    },
    multiple: {
      type: [Boolean],
      default: false,
    },
    fluid: {
      type: [Boolean],
      default: false,
    },
    native: {
      type: [Boolean],
      default: false,
    },
    selected: {
      type: [Object, Boolean],
      default: false,
    },
    map: {
      type: [Object, Boolean],
      default: false,
    },
    onSelect: {
      type: [Function, Boolean],
      default: false,
    },
    selectableData: {
      type: [Array, Boolean],
      default: false,
    },
    value: [Object, String],
    size: [String],
    returnValue: [Boolean],
  },
  components: {
    VListItem,
  },
  data() {
    return {
      events: {
        click: false,
      },
      selectedItem: {},
      defaultTitle: 'Choose an option',
      active: false,
      selectedItemIndex: -1,
      position: 'bottom',
      listHeight: 240,
    };
  },
  mounted() {
    window.addEventListener('keyup', this.handleKeyUp);
    if (this.selected) {
      this.selectItemFromProp();
    }
  },
  updated() {
    if (this.selected) {
      if (!this.events.click) {
        this.selectItemFromProp();
      }
      this.events.click = false;
    }
  },
  destroyed() {
    const body = document.getElementsByTagName('body');
    body[0].className = '';
  },
  computed: {
    classList() {
      const {
        fluid, active, native, size, position,
      } = this;
      return {
        'm-dropdown': true,
        'm-dropdown--fluid': fluid,
        'm-dropdown--active': active,
        'm-dropdown--native': native,
        [`m-dropdown--${size}`]: size,
        [`m-dropdown--${position}`]: !native,
      };
    },
    getTitle() {
      if (this.map) {
        if (this.selectedItem[this.map.title] || this.selectedItem.title) {
          if (this.selectedItem.title) {
            return this.selectedItem.title;
          }
          if (this.selected[this.map.title]) {
            return this.selected[this.map.title];
          }
          return this.defaultTitle;
        }
        return this.defaultTitle;
      }
      return this.defaultTitle;
    },
  },
  methods: {
    selectItemFromProp() {
      const itemToSelectKey = Object.keys(this.selected)[0];
      let itemToSelectValue = Object.values(this.selected)[0];
      if (!itemToSelectValue) {
        return;
      }
      if (typeof itemToSelectValue === 'object') {
        itemToSelectValue = itemToSelectValue.title;
      }
      const selectedItem = this.list.filter((item) => {
        if (item[itemToSelectKey] === itemToSelectValue) {
          return item;
        }
        return null;
      })[0];
      this.selectedItem = selectedItem;
      if (this.returnValue && this.map) {
        this.$emit('input', selectedItem[this.map.title]);
      } else {
        this.$emit('input', this.selectedItem);
      }
    },
    focusIn() {
      const body = document.getElementsByTagName('body');
      const listHeight = this.listHeight + 15;
      const spaceBelow = window.innerHeight - this.$el.getBoundingClientRect().bottom;
      body[0].className = 'is-not-scrollable';
      if (spaceBelow > listHeight) {
        this.position = 'bottom';
      } else {
        this.position = 'top';
      }
    },
    focusOut() {
      const body = document.getElementsByTagName('body');
      body[0].className = '';
    },
    selectItem($event) {
      const dataIndex = parseInt($event.target.getAttribute('data-index'), 10);
      const dataId = parseInt($event.target.getAttribute('data-id'), 10);
      this.events.click = true;
      this.selectedItem = {
        title: this.map ? this.list[dataIndex][this.map.title] : this.list[dataIndex].title,
        id: dataId,
      };
      if (this.selectableData) {
        this.selectableData.forEach((item) => {
          this.selectedItem[item] = this.list[dataIndex][item];
        });
      }
      this.selectedItemIndex = dataId;
      this.$refs.listItem.map((item, index) => {
        const listItem = item;
        listItem.selected = index === dataId;
        return item;
      });
      this.$el.blur();
      if (this.onSelect) {
        this.onSelect(this.selectedItem, this.$el);
      }
      if (this.returnValue) {
        this.$emit('input', this.selectedItem.title);
      } else {
        this.$emit('input', this.selectedItem);
      }
    },
    handleKeyUp($event) {
      if (this.$el === document.activeElement) {
        if ($event.key === 'ArrowDown') this.arrowDown();
        if ($event.key === 'ArrowUp') this.arrowUp();
        if ($event.key === 'ArrowUp' || $event.key === 'ArrowDown') {
          const listItems = this.$refs.listItem;
          const { selectedItemIndex } = this;
          this.selectedItem = listItems[selectedItemIndex].$el.innerText;
          listItems.map((item, index) => {
            const listItem = item;
            listItem.selected = index === selectedItemIndex;
            return item;
          });
        }
        if ($event.key === 'Enter') {
          this.$el.blur();
        }
      }
    },
    arrowDown() {
      const listItemHeight = this.$refs.listItem[0].$el.offsetHeight;
      const listItems = this.$refs.listItem;
      if (listItems.length !== (this.selectedItemIndex + 1)) {
        this.selectedItemIndex += 1;
      }
      const listPosition = this.selectedItemIndex * listItemHeight;
      const amountScrolled = this.$refs.list.scrollTop;
      const shouldScrollDown = (listPosition + listItemHeight) > (this.listHeight + amountScrolled);
      if (shouldScrollDown) {
        this.$refs.list.scrollTop += (listItemHeight + 1);
      }
    },
    arrowUp() {
      const listItemHeight = this.$refs.listItem[0].$el.offsetHeight;
      if (this.selectedItemIndex > 0) {
        this.selectedItemIndex -= 1;
      }
      const amountScrolled = this.$refs.list.scrollTop;
      const shouldScrollDown = this.listHeight > amountScrolled;
      if (shouldScrollDown) {
        this.$refs.list.scrollTop -= (listItemHeight + 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../styles/component-utils';

  $parent: '.m-dropdown';

  #{$parent},
  #{$parent} select {
    position: relative;
    border: 1px solid $color-border;
    display: inline-block;
    border-radius: 3px;
    min-width: 180px;
    cursor: pointer;
    transition: border-color .2s ease, opacity .2s ease, box-shadow .2s ease;
    &:active,
    &:focus {
      outline: none;
      border-color: $signable-primary-1;
      box-shadow: 0px 0px 10px -3px $signable-primary-1;
      transition: border-color .2s ease, opacity .2s ease, box-shadow .2s ease;
      z-index: 102;
      #{$parent}__icon {
        color: $signable-primary-1;
        z-index: 103;
      }
      #{$parent}__selected {
        z-index: 103;
      }
      #{$parent}__list {
        max-height: 240px;
        transition:
          max-height .25s ease,
          opacity .2s ease,
          border-color .2s ease,
          box-shadow .2s ease;
        opacity: 1;
        border: 1px solid $signable-primary-1;
        z-index: 101;
      }
    }
    //elements
    &__selected {
      padding: 12px $spacing-large 12px $spacing-small;
      background-color: $signable-secondary-4-shade-3;
      position: relative;
      z-index: 1;
      border-radius: 3px;
      transition: none;
    }
    &__icon {
      @include alignElement(false, true);
      z-index: 1;
      right: $spacing-small;
      height: 10px;
      width: 10px;
      pointer-events: none;
      color: $signable-secondary-3-shade-2;
    }
    &__list {
      position: absolute;
      background-color: $signable-secondary-4-shade-3;
      max-height: 0;
      opacity: 0;
      overflow: hidden;
      width: calc(100% + 2px);
      left: -1px;
      box-shadow: 0px 0px 10px -3px $signable-primary-1;
      transition: max-height .25s ease, opacity .2s ease, border-color .2s ease;
      overflow: auto;
      border: 1px solid transparent;
      z-index: 100;
      /deep/ .a-list-item span {
        pointer-events: none;
      }
    }
    //modifiers
    &--fluid {
      width: 100%;
    }
    &--native {
      padding: 0;
      border: 0;
      &:active,
      &:focus {
        border: 0;
        box-shadow: 0;
      }
      select {
        -webkit-appearance: none;
        padding: 12px;
        font: inherit;
        font-size: inherit;
        color: inherit;
        background-color: $signable-secondary-4-shade-3;
        &:active,
        &:focus {
          + #{$parent}__icon {
            color: $signable-primary-1;
            z-index: 200;
          }
        }
      }
    }
    &--top {
      transition: border-color .2s ease, border-top-color 0s ease, border-radius .4s ease;
      &:active,
      &:focus {
        border-top: 0;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        transition: border-color .2s ease, border-bottom-color 0s ease, border-radius 0s ease;
        #{$parent}__selected {
          border-top: 1px solid $color-border;
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          transition: none;
        }
        #{$parent}__icon {
          margin-top: 1px;
        }
        #{$parent}__list {
          border-bottom: 0;
        }
      }
      #{$parent}__list {
        bottom: 44px;
        border-bottom: 0;
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
      }
    }
    &--bottom {
      transition: border-color .2s ease, border-bottom-color 0s ease, border-radius .4s ease;
      &:active,
      &:focus {
        border-bottom: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        transition: border-color .2s ease, border-bottom-color 0s ease, border-radius 0s ease;
        #{$parent}__selected {
          border-bottom: 1px solid $color-border;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          transition: none;
        }
        #{$parent}__list {
          border-top: 0;
        }
      }
      #{$parent}__list {
        top: 44px;
        border-top: 0;
        border-bottom-left-radius: 3px;
        border-bottom-right-radius: 3px;
      }
    }
    &--error {
      border-color: $color-error;
      #{$parent}__icon {
        color: $color-error;
      }
    }
    &--small {
      #{$parent}__selected {
        padding: 7px $spacing-xs 7px $spacing-small;
      }
      #{$parent}__list {
        bottom: 30px;
        /deep/ .a-list-item--basic {
          padding: 7px $spacing-xs 7px $spacing-small;
        }
      }
    }
  }

</style>
